import React, { useEffect, useRef } from 'react';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { markdownLanguage } from '@codemirror/lang-markdown';
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands';
import { HighlightStyle, LanguageSupport, syntaxHighlighting } from '@codemirror/language';
import { EditorView, keymap } from '@codemirror/view';
import { tags } from '@lezer/highlight';

interface MarkupEditorProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  fillHeight?: boolean;
  readOnly?: boolean;
  focusRequest?: number;
}

const markdownHighlightStyle = HighlightStyle.define([
  { tag: tags.heading1, fontSize: '1.875rem', fontWeight: '700' },
  { tag: tags.heading2, fontSize: '1.5rem', fontWeight: '700' },
  { tag: tags.heading3, fontSize: '1.25rem', fontWeight: '600' },
  { tag: tags.heading4, fontSize: '1.125rem', fontWeight: '600' },
  { tag: tags.heading5, fontSize: '1rem', fontWeight: '600' },
  { tag: tags.heading6, fontSize: '0.875rem', fontWeight: '600' },
  {
    tag: tags.strikethrough,
    textDecoration: 'line-through',
    textDecorationColor: '#6b7280',
    textDecorationThickness: '2px',
  },
]);

const recipeEditorTheme = EditorView.theme({
  '&': {
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: '#ffffff',
    color: '#111827',
    fontSize: '0.875rem',
  },
  '&.cm-focused': {
    outline: 'none',
    borderColor: '#6366f1',
    boxShadow: '0 0 0 1px #6366f1',
  },
  '.cm-scroller': {
    overflow: 'auto',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  },
  '.cm-content': {
    padding: '0.5rem 0',
    caretColor: '#111827',
  },
  '.cm-line': {
    padding: '0 0.75rem',
  },
  '.cm-placeholder': {
    color: '#9ca3af',
  },
  '.cm-gutters': {
    display: 'none',
  },
});

const editorExtensions = [
  new LanguageSupport(markdownLanguage),
  history(),
  keymap.of([...defaultKeymap, ...historyKeymap]),
  syntaxHighlighting(markdownHighlightStyle),
  recipeEditorTheme,
  EditorView.lineWrapping,
  EditorView.contentAttributes.of({
    'aria-label': 'Recipe content',
    'aria-multiline': 'true',
    dir: 'auto',
    spellcheck: 'true',
  }),
];

const naturalHeightTheme = EditorView.theme({
  '&': { minHeight: '28rem' },
  '.cm-scroller': { minHeight: '28rem' },
  '.cm-content': { minHeight: '28rem' },
});

const fillHeightTheme = EditorView.theme({
  '&': { height: '100%', minHeight: '0' },
  '.cm-scroller': { height: '100%', minHeight: '0', overflowY: 'auto', overscrollBehavior: 'contain' },
  '.cm-content': { minHeight: '100%' },
});

const MarkupEditor: React.FC<MarkupEditorProps> = ({ id, value, onChange, placeholder, fillHeight = false, readOnly = false, focusRequest = 0 }) => {
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  useEffect(() => {
    if (!focusRequest) return;
    const frame = window.requestAnimationFrame(() => {
      const editor = editorRef.current;
      if (editor?.editor?.getClientRects().length) editor.view?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusRequest]);

  return <CodeMirror
    ref={editorRef}
    id={id}
    value={value}
    placeholder={placeholder}
    height={fillHeight ? '100%' : undefined}
    minHeight={fillHeight ? '100%' : '28rem'}
    className={fillHeight ? 'h-full min-h-0' : undefined}
    theme="none"
    extensions={fillHeight
      ? [...editorExtensions, fillHeightTheme]
      : [...editorExtensions, naturalHeightTheme]}
    basicSetup={false}
    indentWithTab={false}
    editable={!readOnly}
    onChange={onChange}
  />;
};

export default MarkupEditor;
